import { CheckCircleIcon, ChevronRightIcon, WarningIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  ButtonProps,
  Center,
  Collapse,
  Flex,
  Heading,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import {
  Control,
  FieldErrors,
  useFieldArray,
  UseFormGetValues,
  UseFormRegister,
  UseFormReset,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { InstalledDevice } from '../../../api/api-device';
import { del } from '../../../api/api-helpers';
import { useAppDispatch } from '../../../app/hooks';
import Counter from '../../../common/components/Counter';
import CustomSignalIcon from '../../../common/components/CustomSignalIcon';
import { SubRouteProps } from '../../../common/components/RouteWithSubRoutes';
import {
  removeMeter,
  selectAppliances,
  selectPolarityConfirmationStatus,
  selectLastActiveMeterIndex,
  selectRawMeters,
  selectTestStatusV2,
  setLastActiveMeterIndex,
} from './meterSetupSlice';
import { selectBatteries, selectInverters } from '../system-details/systemDetailsSlice';
import { selectSite } from '../siteSlice';
import {
  SWITCH_CONFIGURABLE_METER_MODELS,
  WIFI_CONFIGURABLE_METER_MODELS,
  checkIfAppliancesAreAssigned,
  checkIfTestsAreComplete,
  getEmptyMeterTemplate,
} from './meter-setup-helpers';
import { MeterSetupFormData } from './meter-setup-form-types';
import { setIsSlidingEnabled } from '../../wizard/wizardSlice';
import MeterDetailsFieldSet from './MeterDetailsFieldSet';

export type Props = {
  reset: UseFormReset<MeterSetupFormData>;
  control: Control<MeterSetupFormData>;
  setError: UseFormSetError<MeterSetupFormData>;
  register: UseFormRegister<MeterSetupFormData>;
  errors: FieldErrors<MeterSetupFormData>;
  setValue: UseFormSetValue<MeterSetupFormData>;
  getValues: UseFormGetValues<MeterSetupFormData>;
};

type MeterDetailsFieldArrayProps = Props & SubRouteProps;

/**
 * The top-level array of fields. Passes each field to a child component, so each set of fields (i.e. each meter) can
 * manage its own state accordingly.
 *
 * @param props
 * @constructor
 */
export default function MeterDetailsFieldArray(props: MeterDetailsFieldArrayProps) {
  const { control } = props;
  const { fields, append, remove } = useFieldArray({
    name: 'meters',
    control,
  });

  const isAllMeterLoaded = fields.every((field) => field.isLoaded);
  const hasSwitchConfigurableMeter =
    fields.findIndex((field) => SWITCH_CONFIGURABLE_METER_MODELS.includes(field.model)) !== -1;
  const hasWifiConfigurableMeter =
    fields.findIndex((field) => WIFI_CONFIGURABLE_METER_MODELS.includes(field.model)) !== -1;
  const dispatch = useAppDispatch();
  const site = useSelector(selectSite);
  const lastActiveMeterIndex = useSelector(selectLastActiveMeterIndex);
  const [expandedIndex, setExpandedIndex] = useState<number>(-1);
  const history = useHistory();
  const appliances = useSelector(selectAppliances);
  const testStatuses = useSelector(selectTestStatusV2);
  const allMeters = useSelector(selectRawMeters);
  const inverters = useSelector(selectInverters);
  const batteries = useSelector(selectBatteries);
  const isAppliancesAssigned = checkIfAppliancesAreAssigned(appliances, inverters, batteries);
  const polarityConfirmationStatus = useSelector(selectPolarityConfirmationStatus);
  useEffect(() => {
    setExpandedIndex(fields.length - 1);
  }, [fields.length]);

  async function handleDeleteMeter(meterId: number | null, serialNumber: string, meterIdx: number) {
    if (window.confirm(`Are you sure you want to delete this meter ${meterIdx + 1}?`)) {
      remove(meterIdx);

      if (meterId && serialNumber) {
        await del<InstalledDevice>(`/fleet/sites/${site.clipsal_solar_id}/devices/${meterId}`, {});
        dispatch(removeMeter(serialNumber));
      }
    }
  }

  return (
    <Box>
      <Flex align="center" justify="space-between" pb={4} px={[4, 6]}>
        <Text pr={8}>How many meters do you want to add in this site?</Text>
        <Counter
          value={fields.length}
          incrementAriaLabel="Add meter"
          decrementAriaLabel="Remove Meter"
          onIncrement={() => append(getEmptyMeterTemplate(`${site.site_name} - Meter ${fields.length + 1}`))}
          onDecrement={() => {
            const lastMeterIndex = fields.length - 1;
            const { meterId, serialNumber } = fields[lastMeterIndex];
            handleDeleteMeter(meterId, serialNumber, lastMeterIndex);
          }}
          isDecrementDisabled={fields.length <= 1}
        />
      </Flex>
      <Accordion
        defaultIndex={lastActiveMeterIndex}
        allowToggle
        index={expandedIndex}
        onChange={(clickedIndex: number) => setExpandedIndex(clickedIndex)}
        data-testid="meter-details-accordion"
      >
        {fields.map((field, meterIndex) => {
          const meter = allMeters[field.serialNumber];
          return (
            <AccordionItem data-testid={`meter-${meterIndex}`} key={`${field.id}-${meterIndex}`}>
              <AccordionButton
                data-testid={`meter-${meterIndex}-accordion-button`}
                py={4}
                onClick={() => dispatch(setLastActiveMeterIndex(meterIndex))}
                role={'button'}
                as={Box}
              >
                <Flex w={'100%'} justify={'space-between'} align={'center'}>
                  <Flex>
                    <Heading data-testid={`meter-${meterIndex}-heading`} size={'md'}>
                      Meter {meterIndex + 1}
                    </Heading>
                    {meter && expandedIndex !== meterIndex && (
                      <Flex ml={2}>
                        <CustomSignalIcon meter={meter} shortenedText />
                      </Flex>
                    )}
                  </Flex>
                  <Flex align={'center'}>
                    <Button
                      data-testid={`meter-${meterIndex}-remove-btn`}
                      variant={'ghost'}
                      onClick={() => handleDeleteMeter(field.meterId, field.serialNumber, meterIndex)}
                      size={'xs'}
                      ml={2}
                      colorScheme="red"
                      aria-label="Delete meter"
                    >
                      Remove
                    </Button>
                    <AccordionIcon />
                  </Flex>
                </Flex>
              </AccordionButton>

              <AccordionPanel pb={4} px={[4, 6]} data-testid={`meter-${meterIndex}-accordion-panel`}>
                <MeterDetailsFieldSet {...{ ...props, meterIndex, field }} />
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>

      <Box>
        <Collapse in={isAllMeterLoaded}>
          <MeterSubFormActionButton
            data-testid="channel-configuration-button"
            onClick={async () => {
              await dispatch(setIsSlidingEnabled(true));
              history.push(`/site/${site.clipsal_solar_id}/meter_setup/configure/ct_configuration`);
            }}
            title="Channel Configuration"
            isComplete={isAppliancesAssigned}
          />

          {hasWifiConfigurableMeter && (
            <MeterSubFormActionButton
              data-testid="wifi-configuration-button"
              onClick={async () => {
                await dispatch(setIsSlidingEnabled(true));
                history.push(`/site/${site.clipsal_solar_id}/meter_setup/configure/wifi_configuration`);
              }}
              title="Wifi Configuration"
              isComplete
              isDisabled={!isAppliancesAssigned}
            />
          )}

          {hasSwitchConfigurableMeter && (
            <MeterSubFormActionButton
              data-testid="switch-configuration-button"
              onClick={async () => {
                await dispatch(setIsSlidingEnabled(true));
                history.push(`/site/${site.clipsal_solar_id}/meter_setup/configure/switch_configuration`);
              }}
              title="Switch Configuration"
              isComplete
              isDisabled={!isAppliancesAssigned}
            />
          )}

          <MeterSubFormActionButton
            data-testid="source-load-test-button"
            onClick={async () => {
              await dispatch(setIsSlidingEnabled(true));
              history.push(`/site/${site.clipsal_solar_id}/meter_setup/configure/meter_tests`);
            }}
            title="Source and Load Test"
            description="Now test sources and loads"
            isComplete={checkIfTestsAreComplete(testStatuses, appliances, polarityConfirmationStatus)}
            isDisabled={!isAppliancesAssigned}
            h={'75px'}
          />
        </Collapse>
      </Box>
    </Box>
  );
}

type MeterSubFormActionButton = {
  isComplete: boolean;
  title: string;
  description?: string;
  'data-testid': string;
} & ButtonProps;

function MeterSubFormActionButton({ isComplete, title, description, ...props }: MeterSubFormActionButton) {
  const borderColor = useColorModeValue('rgba(43, 46, 45, 0.1)', 'rgba(236, 236, 236, 0.1)');
  return (
    <Button
      variant="ghost"
      width={'100%'}
      h={'60px'}
      borderBottom={`1px solid ${borderColor}`}
      px={0}
      rounded={0}
      {...props}
    >
      <Flex w="100%" align="center" justify="space-between">
        <Flex ml={2} flexDir={'column'} justify={'center'} align={'flex-start'}>
          <Heading size={'sm'} fontWeight="500">
            {title}
          </Heading>
          {description && (
            <Text fontWeight="400" fontSize={['sm', 'sm', 'md']} mt={2}>
              {description}
            </Text>
          )}
        </Flex>
        <Center>
          {isComplete ? (
            <CheckCircleIcon mr={2} color={'primaryBranding.500'} w={5} h={5} />
          ) : (
            <WarningIcon mr={2} color={'red.500'} w={5} h={5} />
          )}
          <ChevronRightIcon h={25} w={25} />
        </Center>
      </Flex>
    </Button>
  );
}
